import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import {
  Grid,
  Box,
  Button,
  Card,
  MenuItem,
  Stack,
  Skeleton,
  CardContent,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { alpha } from '@mui/material/styles';
// components
import {
  FormProvider,
  RHFTextField,
  RHFSelect,
  RHFFileUploadField,
} from '../../components/hook-form';
import Iconify from '../../components/Iconify';
import DIOText from '../../components/DIOText';
// hooks
import useResponsive from '../../hooks/useResponsive';
import useAuth from '../../hooks/useAuth';
// sections
import { EmailAccountCreateDialog, HealthInsuranceVerificationDialog } from '../offer';
// swr
import useSWR from 'swr';
// utils
import axiosSWRFetcher from '../../utils/swrFetcher';
import axios from '../../utils/axios';
import {default as axiosNormal} from 'axios';

export default function PersonalContract() {

  const { data, isValidating, mutate } = useSWR(`/api/crews/settings/`, axiosSWRFetcher, {errorRetryInterval: 2000, revalidateOnFocus: false});
  const { enqueueSnackbar } = useSnackbar();
  const { user, getUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [banks, setBanks] = useState([]);
  const [openEmailAccountDialog, setOpenEmailAccountDialog] = useState(false);
  const [openVerificationDialog, setOpenVerificationDialog] = useState(false);

  const smUp = useResponsive('up', 'sm');
  const canCreateEmail = Boolean(user?.can_create_email);

  const UpdatePersonalContractSchema = Yup.object().shape({
    sidejobEmail: Yup.string().email('이메일을 입력하세요'),
    registrationNumber: Yup.string().matches(/^[\d-]*$/, '올바른 주민등록번호를 입력하세요'),
    accountBankCode: Yup.string(),
    accountBankNumber: Yup.string(),
    certificateFile: Yup.mixed(),
  });

  const defaultValues = {
    sidejobEmail: data?.sidejob_email || '',
    registrationNumber: data?.registration_number || '',
    accountBankCode: data?.account_bank_code || '',
    accountBankNumber: data?.account_number || '',
    certificateFile: {id: data?.certificate_uuid || null, name: data?.certificate_name || '', file: data?.certificate_file || ''},
  };

  const methods = useForm({
    resolver: yupResolver(UpdatePersonalContractSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    getValues,
  } = methods;

  useEffect(() => {

    const initialize = async () => {
      try {
        const bankResponse = await axios.get('api/common/bank-codes/');
        const bankData = bankResponse.data.map((r) => ({key: r.code, value: r.name}));
        setBanks(bankData);
      } catch (err) {
        console.error(err);
      }
    }
    if (banks.length === 0) {
      initialize();
    }
    reset({
      sidejobEmail: data?.sidejob_email || '',
      registrationNumber: data?.registration_number || '',
      accountBankCode: data?.account_bank_code || '',
      accountBankNumber: data?.account_number || '',
      certificateFile: {id: data?.certificate_uuid || null, name: data?.certificate_name || '', file: data?.certificate_file || ''},
    });
  }, [data]);

  const fileUploadBeforeSubmit = async (file) => {
    if (file.id && file.name) {
      return {id: file.id, file: file.name, name: file.name}
    } else if (file.name === '' || file.name === null) {
      return {file: null, name: null}
    } else {
      const fileURLResponse = await axios.post(
        `/api/crews/settings/certificates/upload-url/`,
        {filename: file.name}
      );
      const {filename, url} = fileURLResponse.data;

      await axiosNormal.put(
        url,
        file,
        {
          headers: {
            'Content-Type': file.type
          }
        }
      );
      return {file: filename, name: file.name};
    }
  }


  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      const fileUploadedData = await fileUploadBeforeSubmit(formData.certificateFile);
      const postData = {
        ...data,
        username: data.crew?.user_name,
        nickname: data.crew?.nickname,
        account_bank_code: formData.accountBankCode,
        account_number: formData.accountBankNumber,
        certificate_uuid: fileUploadedData.id,
        certificate_file: fileUploadedData.file,
        certificate_name: fileUploadedData.name,
        registration_number: formData.registrationNumber,
      }
      await axios.post(
        `/api/crews/settings/`,
        postData
      );
      enqueueSnackbar('계약 정보가 변경되었습니다.');
      mutate(`/api/crews/settings/`);
      setIsSubmitting(false);
    } catch (error) {
      console.error(error);
      setIsSubmitting(false);
    }
  };

  const openFile = async (item) => {
    const response = await axios.post(`/api/crews/settings/certificates/${item.id}/url/`);
    window.open(response.data.url);
  };

  const handleEmailAccountCreated = async () => {
    setOpenEmailAccountDialog(false);
    await getUser();
    mutate();
  };

  const handleVerificationClose = () => {
    setOpenVerificationDialog(false);
  };

  const handleVerified = () => {
    setOpenVerificationDialog(false);
    mutate();
  };

  return (
    <Grid item xs={12}>
      {(!data || banks.length === 0 || isValidating) && (
      <Stack spacing={3}>
        <Card sx={{ border: (theme) => `1px solid ${theme.palette.grey[500_32]}` }}>
          <CardContent>
            <Stack spacing={3}>
              <Skeleton variant="text" height={56} />
              <Skeleton variant="text" height={56} />
              <Skeleton variant="text" height={56} />
              <Skeleton variant="text" height={56} />
            </Stack>
          </CardContent>
        </Card>
      </Stack>
      )}
      {data && banks.length > 0 && !isValidating && (
        <FormProvider methods={methods}>
          <Stack spacing={3}>
            <Box
              sx={{
                p: 1.5,
                border: '1px solid',
                borderRadius: 1,
                borderColor: (theme) => theme.palette.error.light,
                backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
              }}
            >
              <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                <Iconify icon={'eva:alert-circle-fill'} sx={{ color: "error.main", minWidth: 20 }} width={24} height={24} />
                <Stack spacing={0.5}>
                  <Typography variant={smUp ? "subtitle1" : "subtitle2"} sx={{ color: "error.darker" }}>
                    <DIOText />에서는 본인 명의로만 계약할 수 있습니다.
                  </Typography>
                  <Typography variant={smUp ? "body1" : "body2"} sx={{ color: "error.darker" }}>
                    본인 명의로 '사업 소득'이 발생하면 안되는 상황이라면, <DIOText />를 이용하실 수 없습니다.
                  </Typography>
                </Stack>
              </Stack>
            </Box>
            <Card sx={{ border: (theme) => `1px solid ${theme.palette.grey[500_32]}` }}>
              <CardContent>
                <Stack spacing={3}>
                  <RHFTextField
                    name="sidejobEmail"
                    label="사이드잡 이메일"
                    helperText="신규 계약시, 회사와의 업무에 사용할 이메일을 생성할 수 있습니다"
                    InputProps={{
                      readOnly: true,
                      endAdornment: canCreateEmail ? (
                        <Button
                          variant="contained"
                          onClick={() => setOpenEmailAccountDialog(true)}
                          sx={{ whiteSpace: 'nowrap', boxShadow: 'none' }}
                          disableRipple
                        >
                          생성하기
                        </Button>
                      ) : null,
                    }}
                    disabled={!canCreateEmail}
                  />

                  <RHFTextField
                    name="registrationNumber"
                    label="주민등록번호"
                    helperText={"소득세 세금 신고에 필요합니다"}
                  />
                  <Stack direction={smUp ? 'row' : 'column'} spacing={3}>
                    <RHFSelect
                      fullWidth
                      sx={smUp ? {maxWidth:1, minWidth:200} : {}}
                      name="accountBankCode"
                      label="인건비 지급받을 은행"
                      SelectProps={{ native: false }}
                    >
                      {banks.map((option) => (
                        <MenuItem
                          key={option.key}
                          value={option.key}
                          sx={{
                            mx: 1,
                            my: 0.5,
                            borderRadius: 0.75,
                            typography: 'body2',
                          }}
                        >
                          {option.value}
                        </MenuItem>
                      ))}
                    </RHFSelect>
                    <RHFTextField
                      name="accountBankNumber"
                      label="인건비 지급받을 계좌번호"
                    />
                  </Stack>
                  <RHFFileUploadField
                    link
                    name="certificateFile"
                    label="신분증 사본"
                    item={{file: getValues("certificateFile")}}
                    openfile={() => openFile(getValues("certificateFile"))}
                    helperText="파일을 선택하고 아래 저장하기를 눌러주세요"
                    outlinedButton
                  />
                </Stack>
                <Stack alignItems="flex-end" sx={{ mt: 3 }}>
                  <LoadingButton
                    onClick={handleSubmit(onSubmit)}
                    type="button"
                    variant="contained"
                    disabled={isValidating}
                    loading={isSubmitting}
                    size="large"
                  >
                    저장하기
                  </LoadingButton>
                </Stack>
              </CardContent>
            </Card>

            {/* 건강보험 인증 카드 - 토스페이먼츠 계약자만 */}
            {data.has_tosspayments_contract && (
              <Card sx={{ border: (theme) => `1px solid ${theme.palette.grey[500_32]}` }}>
                <CardContent>
                  <Stack direction="row" spacing={1}>
                    <Iconify
                      icon={data.health_insurance_verified ? 'eva:checkmark-circle-fill' : 'eva:shield-fill'}
                      sx={{
                        color: data.health_insurance_verified ? 'success.main' : 'text.secondary',
                        width: 24,
                        height: 24,
                        minWidth: 24,
                      }}
                    />
                    <Stack
                      sx={{ width: 1 }}
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      spacing={1.5}
                    >
                      <Stack>
                        <Typography
                          variant="body2"
                          sx={{
                            color: data.health_insurance_verified ? 'text.disabled' : 'text.primary',
                            textDecoration: data.health_insurance_verified ? 'line-through' : 'none',
                          }}
                        >
                          <b>건강보험 인증</b>으로 경력을 확인해주세요
                        </Typography>
                        {!data.health_insurance_verified ? (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            신규 계약 시 건강보험공단 자격득실확인서를 통해 경력을 검증합니다
                          </Typography>
                        ) : (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            인증시 1년간 재인증이 필요하지 않습니다
                          </Typography>
                        )}
                      </Stack>
                      {!data.health_insurance_verified ? (
                        <Button
                          variant="contained"
                          onClick={() => setOpenVerificationDialog(true)}
                          disableRipple
                          size="large"
                        >
                          인증하기
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          disableRipple
                          disabled
                          size="large"
                        >
                          인증완료
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Stack>
        </FormProvider>
      )}

      <EmailAccountCreateDialog
        open={openEmailAccountDialog}
        onClose={() => setOpenEmailAccountDialog(false)}
        onCreated={handleEmailAccountCreated}
      />

      <HealthInsuranceVerificationDialog
        open={openVerificationDialog}
        onClose={handleVerificationClose}
        userName={user?.crew?.name || ''}
        onVerified={handleVerified}
      />
    </Grid>
  )
}
